import {
	addEdge,
	applyEdgeChanges,
	applyNodeChanges,
	type Connection,
	MarkerType,
	type OnEdgesChange,
	type OnNodesChange,
	type ReactFlowInstance,
	reconnectEdge,
	type XYPosition,
} from "@xyflow/react";
import { useCallback, useRef, useState } from "react";

import { RULE_CANVAS_GRID_SIZE } from "../canvas/constants";
import { DEFAULT_EDGE_COLOR } from "../edges/BaseEdge";
import { alignNodesToGrid, buildGhostNode, deleteNodePreservingDecisionEdges, snapPositionToGrid } from "../nodes/nodeFactory";
import { reconnectableForEdgeType, type RuleEdge, type RuleEdgeType } from "../types/edges";
import type { RuleNode } from "../types/nodes";

const useRuleFlow = () => {
	const [nodes, setNodes] = useState<RuleNode[]>([]);
	const [edges, setEdges] = useState<RuleEdge[]>([]);
	const nodesRef = useRef(nodes);
	const edgesRef = useRef(edges);
	nodesRef.current = nodes;
	edgesRef.current = edges;
	const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance<RuleNode, RuleEdge> | null>(null);

	const handleFlowInit = useCallback((instance: ReactFlowInstance<RuleNode, RuleEdge>) => {
		setReactFlowInstance(instance);
	}, []);

	const handleSaveFlow = useCallback(() => {

	}, [reactFlowInstance]);

	const handleRestoreFlow = useCallback(() => {

	}, [reactFlowInstance]);

	const onNodesChange: OnNodesChange<RuleNode> = useCallback((changes) => {
		const removeChanges = changes.filter((c): c is { type: "remove"; id: string } => c.type === "remove");
		const otherChanges = changes.filter((c) => c.type !== "remove");

		if (removeChanges.length === 0) {
			setNodes((nodesSnapshot) => alignNodesToGrid(applyNodeChanges(changes, nodesSnapshot)));
			return;
		}

		let nextNodes = nodesRef.current;
		let nextEdges = edgesRef.current;
		for (const rc of removeChanges) {
			if (!nextNodes.some((n) => n.id === rc.id)) continue;
			const result = deleteNodePreservingDecisionEdges(rc.id, nextNodes, nextEdges);
			nextNodes = result.nodes;
			nextEdges = result.edges;
		}
		if (otherChanges.length > 0) {
			nextNodes = alignNodesToGrid(applyNodeChanges(otherChanges, nextNodes));
		} else {
			nextNodes = alignNodesToGrid(nextNodes);
		}
		setNodes(nextNodes);
		setEdges(nextEdges);
	}, []);

	const onEdgesChange: OnEdgesChange<RuleEdge> = useCallback(
		(changes) => setEdges((edgesSnapshot) => applyEdgeChanges(changes, edgesSnapshot)),
		[],
	);

	const onConnect = useCallback((params: Connection) => {
		let edgeType: RuleEdgeType = "default";

		/**
		 * Important - sourceHandle dictates edge type!
		 */
		if (params.sourceHandle === "true") edgeType = "decision-true";
		if (params.sourceHandle === "false") edgeType = "decision-false";
		if (params.sourceHandle === "loop-start") edgeType = "loop-start";
		// Loop exit on main canvas: default edge + handle chrome; persist as endLoop via transitionWhenFromMainCanvasEdge.
		if (params.sourceHandle === "loop-end") edgeType = "default";

		const reconnectable = reconnectableForEdgeType(edgeType);

		// This creates the arrowhead at the end of the edge!
		setEdges((edgesSnapshot) =>
			addEdge(
				{
					...params,
					type: edgeType,
					...(reconnectable ? { reconnectable } : {}),
					markerEnd: {
						type: MarkerType.Arrow,
						color: DEFAULT_EDGE_COLOR,
						height: 28,
						width: 28,
					},
				},
				edgesSnapshot,
			),
		);
	}, []);

	const getAnchoredMenuPosition = useCallback(
		(anchorEl: HTMLElement | null): XYPosition | null => {
			if (!anchorEl || !reactFlowInstance) {
				return null;
			}

			const rect = anchorEl.getBoundingClientRect();
			const projectedPosition = reactFlowInstance.screenToFlowPosition({
				x: rect.left + rect.width / 2,
				y: rect.bottom + RULE_CANVAS_GRID_SIZE * 2,
			});

			return snapPositionToGrid(projectedPosition);
		},
		[reactFlowInstance],
	);


	/**
	 * Tracks whether the drag landed on a valid target. When the user drops
	 * the endpoint on empty canvas space the edge is removed (or, for
	 * decision edges, reconnected to a ghost node so the path stays visible).
	 */
	const reconnectSuccessful = useRef<boolean>(true);

	/**
	 * Holds the edge currently being reconnected so the custom connection-line
	 * component can read its type / label and render the pill during the drag.
	 */
	const reconnectingEdgeRef = useRef<RuleEdge | null>(null);

	const onReconnectStart = useCallback(
		(_event: unknown, edge: RuleEdge) => {
			reconnectSuccessful.current = false;
			reconnectingEdgeRef.current = edge;
		},
		[],
	);

	const onReconnect = useCallback(
		(oldEdge: RuleEdge, newConnection: Connection) => {
			reconnectSuccessful.current = true;
			const oldTargetId = oldEdge.target;

			setEdges((eds) => reconnectEdge(oldEdge, newConnection, eds));

			// Clean up old target if it was a ghost node (now orphaned)
			setNodes((nds) => {
				const oldTarget = nds.find((n) => n.id === oldTargetId);
				if (oldTarget?.type === "ghost") {
					return nds.filter((n) => n.id !== oldTargetId);
				}
				return nds;
			});
		},
		[setEdges, setNodes],
	);

	const onReconnectEnd = useCallback(
		(event: unknown, edge: RuleEdge) => {
			reconnectingEdgeRef.current = null;

			if (!reconnectSuccessful.current) {
				const isDecisionEdge =
					edge.type === "decision-true" || edge.type === "decision-false";
				const srcNode = nodesRef.current.find((n) => n.id === edge.source);
				const isMainLoopExitEdge =
					srcNode?.type === "loop" &&
					(edge.sourceHandle === "out" || edge.sourceHandle === "loop-end") &&
					(edge.type === "default" || edge.type === "loop-end" || edge.type == null);

				if ((isMainLoopExitEdge || isDecisionEdge) && reactFlowInstance) {
					// We need to keep the path visible via a ghost node for decision edges
					const clientX = (event as MouseEvent).clientX;
					const clientY = (event as MouseEvent).clientY;

					const dropPosition = reactFlowInstance.screenToFlowPosition({
						x: clientX,
						y: clientY,
					});

					const oldTargetId = edge.target;

					// Read the current nodes to decide reuse vs. create
					const currentNodes = reactFlowInstance.getNodes();
					const oldTarget = currentNodes.find((n) => n.id === oldTargetId);

					if (oldTarget?.type === "ghost") {
						// If the old target is a ghost node, we can reuse it by moving it to the drop spot
						setNodes((nds) =>
							nds.map((n) =>
								n.id === oldTargetId
									? { ...n, position: snapPositionToGrid(dropPosition) }
									: n,
							),
						);
					} else {
						// If we just disconnected from a real node, we need to create a ghost node and redirect the edge to it to maintain the decision path visibility
						const ghostNode = buildGhostNode(dropPosition);

						setEdges((eds) =>
							eds.map((e) =>
								e.id === edge.id
									? { ...e, target: ghostNode.id, targetHandle: null }
									: e,
							),
						);

						setNodes((nds) => [...nds, ghostNode]);
					}
				} else {
					setEdges((eds) => eds.filter((e) => e.id !== edge.id));
				}
			}
		},
		[setEdges, setNodes, reactFlowInstance],
	);

	return {
		nodes,
		edges,
		reactFlowInstance,
		setNodes,
		setEdges,
		handleFlowInit,
		handleSaveFlow,
		handleRestoreFlow,
		onNodesChange,
		onEdgesChange,
		onConnect,
		getAnchoredMenuPosition,
		onReconnect,
		onReconnectStart,
		onReconnectEnd,
		reconnectingEdgeRef,
	};
};

export default useRuleFlow;
